import { takeHomePay, incomeTax, capitalGains, salesTax } from "../../lib/calculators/tax-calcs";
import { PROVINCE_LIST, PROVINCE_LIST_SALES } from "../../lib/financial-data/canada";
import { CAD } from "../../lib/format";

const prov = PROVINCE_LIST.map((p) => ({ value: p.code, label: p.name }));
const provSales = PROVINCE_LIST_SALES.map((p) => ({ value: p.code, label: p.name }));
const base = (extra) => ({ applicableYear: 2026, lastUpdated: "2026-01-01", category: "tax", icon: "Receipt", ...extra });

export const takeHomePayConfig = base({
  slug: "take-home-pay-calculator-canada",
  name: "Take-Home Pay Calculator",
  tagline: "See your net paycheque after tax, CPP and EI.",
  recommendedProduct: "personal-finance-dashboard",
  relatedTools: ["income-tax-calculator-canada", "budget-calculator-canada"],
  seo: { title: "Take-Home Pay Calculator Canada (2026) — Net Paycheque After Deductions", description: "Calculate your net take-home pay in Canada after federal & provincial tax, CPP and EI, for any pay frequency and province.", keywords: ["take home pay calculator canada", "net pay calculator", "paycheck calculator canada", "salary after tax"] },
  inputs: [
    { id: "grossAnnual", label: "Gross annual salary", type: "currency", default: 75000, min: 0 },
    { id: "province", label: "Province / territory", type: "select", default: "ON", options: prov },
    { id: "payFrequency", label: "Pay frequency", type: "select", default: 26, options: [{ value: 12, label: "Monthly" }, { value: 24, label: "Semi-monthly" }, { value: 26, label: "Bi-weekly" }, { value: 52, label: "Weekly" }] },
    { id: "rrspContribution", label: "Annual RRSP contribution", type: "currency", default: 0, min: 0, help: "Reduces taxable income." },
  ],
  compute: (v) => {
    const r = takeHomePay(v);
    return {
      primary: { label: "Net pay per cheque", value: r.netPerPay, type: "currency2" },
      secondary: [
        { label: "Net annual pay", value: r.netAnnual, type: "currency" },
        { label: "Income tax", value: r.incomeTax, type: "currency" },
        { label: "CPP", value: r.cpp, type: "currency" },
        { label: "EI", value: r.ei, type: "currency" },
        { label: "Average tax rate", value: r.averageRate, type: "percent" },
      ],
      chart: { type: "pie", title: "Where your salary goes", data: [{ name: "Take-home", value: Math.round(r.netAnnual) }, { name: "Income tax", value: Math.round(r.incomeTax) }, { name: "CPP", value: Math.round(r.cpp) }, { name: "EI", value: Math.round(r.ei) }] },
    };
  },
  content: {
    howItWorks: "Your employer withholds federal and provincial income tax plus CPP and EI premiums from each paycheque. This tool estimates those deductions for 2026 and divides your net annual pay by your pay frequency.",
    inputsExplained: [{ term: "CPP", desc: "5.95% on earnings between $3,500 and $74,600, plus CPP2 above that." }, { term: "EI", desc: "1.63% on insurable earnings up to $68,900 for 2026." }],
    example: "A $75,000 salary in Ontario nets roughly $56,000/year, or about $2,150 bi-weekly.",
    assumptions: ["2026 tax brackets, CPP and EI rates.", "Only basic deductions — ignores other credits.", "Assumes employment income."],
    limitations: ["Doesn't include benefits, union dues or extra withholdings.", "Self-employed pay both halves of CPP."],
  },
  faq: [
    { q: "Why is my take-home less than expected?", a: "CPP and EI come off on top of income tax, and higher earners hit higher marginal brackets. This tool shows each deduction separately." },
    { q: "Does an RRSP contribution increase my take-home?", a: "It lowers your taxable income, so less tax is withheld/owed — increasing your effective take-home after any refund." },
  ],
});

export const incomeTaxConfig = base({
  slug: "income-tax-calculator-canada",
  name: "Income Tax Calculator",
  tagline: "Estimate your total federal + provincial income tax.",
  recommendedProduct: "personal-finance-dashboard",
  relatedTools: ["take-home-pay-calculator-canada", "rrsp-tax-savings-calculator-canada"],
  seo: { title: "Income Tax Calculator Canada (2026) — Federal & Provincial", description: "Estimate your 2026 Canadian income tax by province, with average and marginal rates and after-tax income.", keywords: ["income tax calculator canada", "canada tax calculator 2026", "marginal tax rate", "after tax income"] },
  inputs: [
    { id: "income", label: "Taxable income", type: "currency", default: 85000, min: 0 },
    { id: "rrspDeduction", label: "RRSP / other deductions", type: "currency", default: 0, min: 0 },
    { id: "province", label: "Province / territory", type: "select", default: "ON", options: prov },
  ],
  compute: (v) => {
    const r = incomeTax(v);
    return {
      primary: { label: "Total income tax", value: r.totalTax, type: "currency" },
      secondary: [
        { label: "After-tax income", value: r.afterTax, type: "currency" },
        { label: "Federal tax", value: r.federalTax, type: "currency" },
        { label: "Provincial tax", value: r.provincialTax, type: "currency" },
        { label: "Average rate", value: r.averageRate, type: "percent" },
        { label: "Marginal rate", value: r.marginalRate, type: "percent" },
      ],
      chart: { type: "pie", title: "Income split", data: [{ name: "After tax", value: Math.round(r.afterTax) }, { name: "Federal tax", value: Math.round(r.federalTax) }, { name: "Provincial tax", value: Math.round(r.provincialTax) }] },
    };
  },
  content: {
    howItWorks: "Canada taxes income in progressive brackets — federally and provincially. This tool applies the 2026 brackets to your taxable income (after any deductions) and shows your total tax, average rate and marginal rate.",
    inputsExplained: [{ term: "Average rate", desc: "Total tax ÷ income — your overall tax burden." }, { term: "Marginal rate", desc: "The rate on your next dollar of income." }],
    example: "$85,000 in Ontario for 2026 owes roughly $19,000 in combined income tax — about a 22% average rate.",
    assumptions: ["2026 federal + provincial brackets.", "Only the basic structure — ignores most credits.", "Not tax advice."],
    limitations: ["Excludes tax credits (basic personal amount aside), dividends and other income types.", "Consult a professional for filing."],
  },
  faq: [
    { q: "Is this my exact tax bill?", a: "No — it's a close estimate using the tax brackets. Credits and other income types will change your actual return." },
    { q: "What's the difference between average and marginal rate?", a: "Average is your overall rate; marginal is what you pay on your next dollar — useful for deciding on RRSP contributions or a raise." },
  ],
});

export const capitalGainsConfig = base({
  slug: "capital-gains-tax-calculator-canada",
  name: "Capital Gains Tax Calculator",
  tagline: "Estimate the tax on selling investments or property.",
  recommendedProduct: "personal-finance-dashboard",
  relatedTools: ["income-tax-calculator-canada", "investment-fee-calculator-canada"],
  seo: { title: "Capital Gains Tax Calculator Canada (2026) — Investments & Property", description: "Estimate capital gains tax in Canada using the 50% inclusion rate, your province and marginal tax rate.", keywords: ["capital gains tax calculator canada", "capital gains inclusion rate", "tax on selling stocks canada"] },
  inputs: [
    { id: "proceeds", label: "Sale proceeds", type: "currency", default: 100000, min: 0 },
    { id: "acb", label: "Adjusted cost base (what you paid)", type: "currency", default: 60000, min: 0 },
    { id: "otherIncome", label: "Your other taxable income", type: "currency", default: 90000, min: 0 },
    { id: "province", label: "Province / territory", type: "select", default: "ON", options: prov },
    { id: "inclusionRate", label: "Inclusion rate", type: "select", default: 50, options: [{ value: 50, label: "50% (current)" }, { value: 66.6667, label: "66.7% (higher-gain scenario)" }] },
  ],
  compute: (v) => {
    const r = capitalGains(v);
    return {
      primary: { label: "Tax on this capital gain", value: r.taxOwing, type: "currency" },
      secondary: [
        { label: "Capital gain", value: r.capitalGain, type: "currency" },
        { label: "Taxable portion", value: r.taxableGain, type: "currency", hint: `${v.inclusionRate}% included` },
        { label: "After-tax gain", value: r.afterTaxGain, type: "currency" },
        { label: "Effective rate on gain", value: r.effectiveRate, type: "percent" },
      ],
      chart: { type: "pie", title: "Your gain: keep vs tax", data: [{ name: "You keep", value: Math.round(r.afterTaxGain) }, { name: "Tax", value: Math.round(r.taxOwing) }] },
      note: "Only 50% of most capital gains are taxable in Canada. Gains in a TFSA are tax-free; your principal residence is usually exempt.",
    };
  },
  content: {
    howItWorks: "In a non-registered account, 50% of a capital gain is added to your taxable income and taxed at your marginal rate. This tool computes the taxable portion and the extra tax it creates on top of your other income.",
    inputsExplained: [{ term: "Adjusted cost base (ACB)", desc: "Your total cost including purchase price and fees." }, { term: "Inclusion rate", desc: "The taxable share of the gain — 50% currently." }],
    example: "A $40,000 gain at a 30% marginal rate: $20,000 is taxable → about $6,000 in tax.",
    assumptions: ["50% inclusion rate by default.", "Gain stacks on top of your other income.", "Non-registered account."],
    limitations: ["No tax on TFSA gains or (usually) a principal residence.", "Doesn't model capital losses or the lifetime exemption."],
  },
  faq: [
    { q: "How much of a capital gain is taxed?", a: "Currently 50% of the gain is included in income and taxed at your marginal rate. The rest is tax-free." },
    { q: "Are gains in my TFSA or RRSP taxed?", a: "TFSA gains are completely tax-free. RRSP growth is tax-deferred and taxed as income only on withdrawal — not as a capital gain." },
  ],
});

export const salesTaxConfig = base({
  slug: "sales-tax-calculator-canada",
  name: "Sales Tax Calculator (GST/HST/PST)",
  tagline: "Add GST, HST, PST or QST to any amount by province.",
  icon: "Receipt",
  recommendedProduct: "personal-finance-dashboard",
  relatedTools: ["take-home-pay-calculator-canada", "car-loan-calculator-canada"],
  seo: { title: "Sales Tax Calculator Canada (2026) — GST, HST, PST & QST", description: "Calculate GST/HST/PST/QST on any purchase by Canadian province. See the tax amount and total instantly.", keywords: ["sales tax calculator canada", "gst hst calculator", "pst calculator", "how much tax on purchase"] },
  inputs: [
    { id: "amount", label: "Amount (before tax)", type: "currency", default: 100, min: 0 },
    { id: "province", label: "Province / territory", type: "select", default: "ON", options: provSales },
  ],
  compute: (v) => {
    const r = salesTax(v);
    return {
      primary: { label: "Total with tax", value: r.total, type: "currency2" },
      secondary: [
        { label: "Subtotal", value: r.subtotal, type: "currency2" },
        { label: `Tax (${r.taxType} ${r.taxRate}%)`, value: r.taxAmount, type: "currency2" },
        { label: "GST portion", value: r.gst, type: "currency2" },
        { label: "PST/QST portion", value: r.pst, type: "currency2" },
      ],
      chart: { type: "pie", title: "Subtotal vs tax", data: [{ name: "Subtotal", value: Math.round(r.subtotal) }, { name: "Tax", value: Math.round(r.taxAmount) }] },
    };
  },
  content: {
    howItWorks: "Sales tax in Canada depends on the province: some charge a combined HST, others charge GST plus a separate PST (or QST in Quebec), and territories charge GST only. This tool applies the correct 2026 combined rate.",
    inputsExplained: [{ term: "HST provinces", desc: "ON (13%), NS (14%), NB/NL/PE (15%) — one combined tax." }, { term: "GST+PST", desc: "BC/MB (12%), SK (11%), plus Quebec's GST+QST (14.975%)." }],
    example: "A $100 purchase in Ontario costs $113.00; in Alberta it's $105.00 (GST only).",
    assumptions: ["Standard combined rates for taxable goods/services.", "Some items are zero-rated or exempt."],
    limitations: ["Doesn't handle point-of-sale rebates or exempt categories.", "Rates can change — verify for large purchases."],
  },
  faq: [
    { q: "What's the difference between GST, HST and PST?", a: "GST is the 5% federal tax. HST combines federal + provincial into one. PST/QST are separate provincial taxes added on top of GST." },
    { q: "Which province has the lowest sales tax?", a: "Alberta and the three territories charge only 5% GST — the lowest in Canada." },
  ],
});
